import { useEffect, useRef, useState } from 'react'

interface UseSoundPlayProps {
  audioSrc: string
  isLoop?: boolean
}

const useSoundPlayer = ({ audioSrc, isLoop = false }: UseSoundPlayProps) => {
  const [isReady, setIsReady] = useState<boolean>(false)
  const [isPlayingLoop, setIsPlayingLoop] = useState<boolean>(isLoop)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const audioRef = useRef<HTMLAudioElement>()

  useEffect(() => {
    audioRef.current = new Audio(audioSrc)
    audioRef.current.loop = isPlayingLoop
    audioRef.current.oncanplay = () => {
      setIsReady(true)
    }
    audioRef.current.onplay = () => {
      setIsPlaying(true)
    }
    audioRef.current.onended = () => {
      setIsPlaying(false)
    }
    audioRef.current.onpause = () => {
      setIsPlaying(false)
    }

    return () => {
      audioRef.current?.pause()
    }
  }, [audioSrc])

  const setPlayingLoop = (_isLoop: boolean) => {
    if (!audioRef.current) {
      return false
    }
    audioRef.current.loop = _isLoop
    setIsPlayingLoop(_isLoop)
    return true
  }

  const play = async (): Promise<boolean> => {
    if (!audioRef.current) {
      return false
    }
    try {
      await audioRef.current.play()
      return true
    } catch {
      return false
    }
  }

  const stop = () => {
    if (!audioRef.current) {
      return false
    }
    audioRef.current.pause()
    audioRef.current.currentTime = 0
    return true
  }

  return {
    isReady,
    isPlayingLoop,
    setPlayingLoop,
    isPlaying,
    play,
    stop
  }
}

export default useSoundPlayer
